import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, Shield, BookOpen, Globe, Landmark, FlaskConical,
  TrendingUp, Swords, Trophy, ChevronRight, ArrowLeft, Loader2, X, Newspaper,
  Brain, Calculator, Microscope, Languages,
  CheckCircle2, StickyNote, RotateCcw,
} from "lucide-react";
import gkApi from "../api/gk";
import SEOHead from "../components/SEOHead";

/* ── Category icon + color map (generic fallback included) ─────────────────── */
const CAT_META = {
  "history":            { icon: Swords,       color: "#92400E", bg: "#FEF3C7", border: "#F59E0B" },
  "polity":             { icon: Landmark,     color: "#1E40AF", bg: "#EFF6FF", border: "#3B82F6" },
  "geography":          { icon: Globe,        color: "#065F46", bg: "#ECFDF5", border: "#10B981" },
  "science-technology": { icon: FlaskConical, color: "#5B21B6", bg: "#F5F3FF", border: "#8B5CF6" },
  "economy":            { icon: TrendingUp,   color: "#9A3412", bg: "#FFF7ED", border: "#F97316" },
  "defence-policy":     { icon: Shield,       color: "#1A2810", bg: "#F2F6EA", border: "#4A5E2A" },
  "defence-honours":    { icon: Trophy,       color: "#92400E", bg: "#FFFBEB", border: "#D97706" },
  "ssb-preparation":    { icon: Brain,        color: "#1E3A5F", bg: "#EFF6FF", border: "#2563EB" },
  "mathematics":        { icon: Calculator,   color: "#1F2937", bg: "#F3F4F6", border: "#6B7280" },
  "general-science":    { icon: Microscope,   color: "#065F46", bg: "#ECFDF5", border: "#059669" },
  "english":            { icon: Languages,    color: "#4C1D95", bg: "#F5F3FF", border: "#7C3AED" },
  "default":            { icon: BookOpen,     color: "#374151", bg: "#F9FAFB", border: "#9CA3AF" },
};

function getCatMeta(category) {
  return CAT_META[category] || CAT_META.default;
}

const EXAMS = ["NDA", "CDS", "AFCAT", "CAPF", "TA", "SSB"];

const PRACTICE_CATS = new Set(["ssb-preparation", "mathematics", "english"]);

const PRACTICE_LABEL = {
  "english":         { label: "English",     color: "#4C1D95", bg: "#7C3AED" },
  "mathematics":     { label: "Mathematics", color: "#1F2937", bg: "#6B7280" },
  "ssb-preparation": { label: "SSB Prep",    color: "#1E3A5F", bg: "#2563EB" },
};

/* ── Shared header used across all GK pages ──────────────────────────────── */
function GkHeader({ active = "gk", category = "" }) {
  const navigate = useNavigate();
  const practice = PRACTICE_LABEL[category];
  const isGk     = !practice;

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center h-[58px] gap-3">
        <button onClick={() => navigate("/")}
          className="flex items-center gap-1.5 text-sm font-semibold text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0">
          <ArrowLeft size={15} /> Home
        </button>
        <span className="text-gray-200">|</span>

        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate("/ca")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              active === "ca"
                ? "bg-red-600 text-white shadow-sm"
                : "text-gray-500 hover:bg-red-50 hover:text-red-700"
            }`}>
            <Newspaper size={12} />
            Current Affairs
            {active !== "ca" && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
          </button>

          {isGk ? (
            <button
              onClick={() => navigate("/gk")}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all text-white shadow-sm"
              style={{ background: "#1A2810" }}>
              <BookOpen size={12} />
              GK Library
            </button>
          ) : (
            <button
              onClick={() => navigate(`/gk/${category}`)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all text-white shadow-sm"
              style={{ background: practice.bg }}>
              <BookOpen size={12} />
              {practice.label}
            </button>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => navigate("/my-notes")}
            className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg border border-yellow-300 text-yellow-700 bg-yellow-50 hover:bg-yellow-100 transition-all">
            <StickyNote size={12} /> My Notes
          </button>
          <button onClick={() => navigate("/login")}
            className="text-xs font-bold text-white px-4 py-1.5 rounded-lg transition-all"
            style={{ background: "#C9A227" }}>
            Start Free →
          </button>
        </div>
      </div>
    </header>
  );
}

export { GkHeader };

/* ── Main GK Library page ────────────────────────────────────────────────── */
export default function GkLibrary() {
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  const [searchQ, setSearchQ]         = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching]     = useState(false);
  const [activeExam, setActiveExam]   = useState("");

  useEffect(() => {
    gkApi.getCategories()
      .then(setCategories)
      .catch(() => setError("Failed to load categories. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  const doSearch = useCallback(async (q, exam) => {
    if (!q.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const result = await gkApi.search(q, { examTag: exam || undefined });
      setSearchResults(result);
    } catch {
      setSearchResults({ topics: [], pagination: { total: 0 } });
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(searchQ, activeExam), 350);
    return () => clearTimeout(t);
  }, [searchQ, activeExam, doSearch]);

  const examFiltered = categories.filter(c => !activeExam || (c.examTags || []).includes(activeExam));
  const gkCategories = examFiltered.filter(c => !PRACTICE_CATS.has(c._id));

  return (
    <div className="min-h-screen bg-[#F5F6FA] font-body">
      <SEOHead
        title="GK Library for NDA, CDS & CAPF | History, Geography, Polity, Science | EduPulseAI"
        description="Complete GK Library for NDA, CDS, AFCAT and CAPF exam preparation. Free access to History, Geography, Indian Polity, Economy, Science & Technology, Environment topics with MCQ practice."
        keywords="GK for NDA, GK for CDS, general knowledge NDA, history for NDA, geography for CDS, polity for CAPF, science for AFCAT, GK library defence exam"
        url="/gk"
        jsonLd={{ "@context": "https://schema.org", "@type": "CollectionPage", "name": "GK Library for Defence Exams", "url": "https://edupulseai.co.in/gk", "publisher": { "@type": "Organization", "name": "EduPulseAI" } }}
      />
      <GkHeader />

      {/* Hero banner */}
      <div className="py-10 px-4 text-center" style={{ background: "linear-gradient(135deg,#1A2810,#243018)" }}>
        <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: "#C9A227" }}>
          NCERT · Lucent · PIB · Manual
        </p>
        <h1 className="font-sora font-black text-2xl md:text-3xl text-white mb-2">GK Library</h1>
        <p className="text-sm mb-6" style={{ color: "#A8B89A" }}>
          {categories.reduce((s, c) => s + (c.count || 0), 0)} topics across {categories.length} subjects — free to read
        </p>

        {/* Search bar */}
        <div className="max-w-xl mx-auto relative mb-5">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
            placeholder="Search topics — Param Vir Chakra, Fundamental Rights, Panipat…"
            className="w-full pl-9 pr-10 py-3 rounded-xl text-sm bg-white border border-gray-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
          {searchQ && (
            <button onClick={() => { setSearchQ(""); setSearchResults(null); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X size={15} />
            </button>
          )}
        </div>

        {/* Exam filter chips */}
        <div className="flex flex-wrap justify-center gap-2">
          {["", ...EXAMS].map(exam => (
            <button key={exam} onClick={() => setActiveExam(exam)}
              className="text-xs font-bold px-3 py-1.5 rounded-full transition-all"
              style={activeExam === exam
                ? { background: "#C9A227", color: "#1A2810" }
                : { background: "rgba(255,255,255,0.1)", color: "#A8B89A", border: "1px solid rgba(255,255,255,0.15)" }}>
              {exam || "All Exams"}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">

        {/* ── Search results ── */}
        {(searchQ || searching) && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-sora font-bold text-base text-gray-800">
                {searching ? "Searching…" : `${searchResults?.pagination?.total ?? 0} results for "${searchQ}"`}
              </h2>
              {!searching && searchResults?.topics?.length > 0 && (
                <span className="text-xs text-gray-400">{activeExam ? `Filtered: ${activeExam}` : ""}</span>
              )}
            </div>

            {searching ? (
              <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-gray-400" /></div>
            ) : searchResults?.topics?.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {searchResults.topics.map((t, i) => (
                  <TopicCard key={i} topic={t} onClick={() => navigate(`/gk/${t.category}/${t.slug}`)} />
                ))}
              </div>
            ) : (
              <div className="text-center py-10 text-gray-400 text-sm">No topics found. Try different keywords.</div>
            )}
          </div>
        )}

        {/* ── Category grid ── */}
        {!searchQ && (
          <>
            {loading && (
              <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-gray-300" /></div>
            )}
            {error && (
              <div className="text-center py-10 text-red-500 text-sm">{error}</div>
            )}
            {!loading && !error && (
              <>
                {activeExam && (
                  <p className="text-sm text-gray-500 mb-4">
                    Showing categories relevant to <strong className="text-gray-800">{activeExam}</strong>
                    {" · "}<button onClick={() => setActiveExam("")} className="text-yellow-600 underline">Clear filter</button>
                  </p>
                )}

                {/* ── GK Categories ── */}
                <CategoryGrid cats={gkCategories} navigate={navigate} />

                {gkCategories.length === 0 && (
                  <div className="text-center py-10 text-gray-400 text-sm">
                    No GK categories found for <strong>{activeExam}</strong>.{" "}
                    <button onClick={() => setActiveExam("")} className="text-yellow-600 underline">Show all</button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Contribute CTA */}
        <div className="mt-12 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4"
          style={{ background: "linear-gradient(135deg,#1A2810,#243018)" }}>
          <div>
            <p className="font-sora font-bold text-white text-base mb-1">Content grows with the community</p>
            <p className="text-sm" style={{ color: "#A8B89A" }}>
              All content sourced from NCERT, Lucent, PIB and verified by educators. New topics added daily.
            </p>
          </div>
          <button onClick={() => navigate("/login")}
            className="flex-shrink-0 text-sm font-extrabold px-6 py-2.5 rounded-xl transition-all"
            style={{ background: "#C9A227", color: "#1A2810" }}>
            Create Free Account →
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Category card grid ─────────────────────────────────────────────────────── */
function CategoryGrid({ cats, navigate }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {cats.map((cat, i) => {
        const meta = getCatMeta(cat._id);
        const Icon = meta.icon;
        return (
          <motion.div key={cat._id}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: i * 0.04 }}>
            <button
              onClick={() => navigate(`/gk/${cat._id}`)}
              className="w-full text-left rounded-2xl p-5 transition-all hover:shadow-lg hover:-translate-y-0.5 group"
              style={{ background: meta.bg, border: `1.5px solid ${meta.border}40` }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                style={{ background: `${meta.border}20` }}>
                <Icon size={18} style={{ color: meta.color }} />
              </div>
              <p className="font-sora font-extrabold text-[14px] mb-1" style={{ color: meta.color }}>
                {cat.categoryLabel}
              </p>
              <p className="text-xs text-gray-500 mb-3">{cat.count} topic{cat.count !== 1 ? "s" : ""}</p>
              <div className="flex flex-wrap gap-1 mb-3">
                {(cat.examTags || []).filter(Boolean).slice(0, 4).map(tag => (
                  <span key={tag} className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                    style={{ background: `${meta.border}18`, color: meta.color }}>
                    {tag}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-1 text-xs font-bold" style={{ color: meta.color }}>
                Browse topics <ChevronRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>
          </motion.div>
        );
      })}
    </div>
  );
}

/* ── Reusable topic card (used in search results + category page) ──────────── */
export function TopicCard({ topic, onClick, progress }) {
  const meta        = getCatMeta(topic.category);
  const diffColor   = { easy: "#059669", medium: "#D97706", hard: "#DC2626" };
  const isCompleted = progress?.completed;
  const hasNote     = !!progress?.note?.trim();
  const isRevisit   = progress?.revisit;

  return (
    <button onClick={onClick}
      className="w-full text-left bg-white rounded-xl p-4 border border-gray-100 hover:border-gray-300 hover:shadow-md transition-all group"
      style={{ borderLeft: `4px solid ${isCompleted ? '#34D399' : meta.border}` }}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="font-sora font-bold text-[13px] text-gray-900 leading-snug group-hover:text-gray-700">
          {topic.title}
        </p>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isRevisit && (
            <span title="Revisit" className="w-5 h-5 rounded-full bg-yellow-50 flex items-center justify-center">
              <RotateCcw size={11} className="text-yellow-500" />
            </span>
          )}
          {hasNote && (
            <span title="Has notes" className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center">
              <StickyNote size={11} className="text-blue-400" />
            </span>
          )}
          {isCompleted && (
            <span title="Completed" className="w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 size={11} className="text-emerald-500" />
            </span>
          )}
          {!isCompleted && (
            <span className="flex-shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: `${diffColor[topic.difficulty]}18`, color: diffColor[topic.difficulty] }}>
              {topic.difficulty}
            </span>
          )}
        </div>
      </div>
      {topic.summary && (
        <p className="text-xs text-gray-500 leading-relaxed mb-2.5 line-clamp-2">{topic.summary}</p>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded"
          style={{ background: `${meta.border}15`, color: meta.color }}>
          {topic.categoryLabel || topic.category}
        </span>
        {(topic.examTags || []).slice(0, 3).map(t => (
          <span key={t} className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded font-semibold">{t}</span>
        ))}
        {isCompleted ? (
          <span className="ml-auto flex items-center gap-1 text-[11px] font-bold text-emerald-600">
            <CheckCircle2 size={12} /> Done
          </span>
        ) : topic.facts?.length > 0 && (
          <span className="ml-auto text-[10px] text-gray-400">{topic.facts.length} facts · {topic.mcqs?.length || 0} MCQs</span>
        )}
      </div>
    </button>
  );
}
